// node modules
import get from 'lodash/get';
// services
import Fetch from 'modules/Fetch';
import AnalyticsService from 'modules/Analytics';
import PlatformService from 'modules/Platform';
// constants
import { EVENTS } from 'modules/Analytics/consts';
import { VERSION } from 'config';
import { ctaThemeMapper } from './const';
// typings
import {
  INotification,
  INotificationAction,
  INotificationActions,
  INotificationProvider,
} from '../../typings';
import {
  FullScreenActions,
  InAppNotification,
  NotificationConfig,
  NotificationResponseData,
} from './typings';

class NotificationService implements INotificationProvider {
  private notifications: INotification[] = [];
  getNotifications = async () => {
    try {
      const response: HttpResponse<NotificationResponseData> =
      await Fetch.request(
        'NOTIFICATION',
        '/v1/notifications',
        'GET_NOTIFICATIONS',
        {
          headers: {
            'X-Device-Details': this.getDeviceDetailsHeader,
          },
        },
      );

      return this.handleResponse(response);
    } catch (error) {
      const errorMessage = `Failed to get notifications ${JSON.stringify(error)}`;
      AnalyticsService.sendEvent(EVENTS.NOTIFICATION_RESPONSE_FAILURE, {
        Error: errorMessage,
      }, true);
    }
  };

  getExpiringNotifications = async () => {
    try {
      const response: HttpResponse<NotificationResponseData> =
      await Fetch.request(
        'NOTIFICATION',
        '/v1/expiring',
        'GET_EXPIRING_NOTIFICATIONS',
        {
          headers: {
            'X-Device-Details': this.getDeviceDetailsHeader,
          },
        },
      );

      return this.handleResponse(response);
    } catch (error) {
      const errorMessage = `Failed to get expiring notifications ${JSON.stringify(error)}`;
      AnalyticsService.sendEvent(
        EVENTS.EXPIRING_NOTIFICATION_RESPONSE_FAILURE,
        {
          Error: errorMessage,
        },
        true,
      );
    }
  };

  markAsRead = async (notification: INotification) => {
    const isHybrid = notification.id.includes('-hybrid');
    const id = isHybrid ? notification.id.split('-hybrid')[0] : notification.id;
    const URL =
      notification?.classification === 1
        ? '/v1/expiring'
        : '/v1/notifications';
    const queryObj = {
      ...(notification?.classification !== 1 && {
        query: {
          classification: encodeURIComponent(notification.classification),
        },
      }),
    };
    const response: HttpResponse<null> = await Fetch.request(
      'NOTIFICATION',
      URL,
      'NOTIFICATIONS_READ',
      {
        pathParameter: id,
        method: 'PUT',
        ...queryObj,
      },
    );

    return response;
  };

  private handleResponse(response: HttpResponse<NotificationResponseData>) {
    if (response.success) {
      // #region customConfigList
      const customConfigList = get(response.data, 'customConfigList', []) || [];
      // process only customConfigList for in-app notifications
      customConfigList.forEach((item) => {
        if (item) {
          try {
            const inAppData: InAppNotification = JSON.parse(
              item.customConfig.inApp,
            );

            this.handleCustomConfig(inAppData, this.notifications, item);
          } catch (error) {
            AnalyticsService.sendEvent(
              EVENTS.NOTIFICATION_RESPONSE_PARSING_ERROR,
            );
          }
        }
      });

      // #endregion

      // #region criticalList for GTT notifications
      // TODO: Uncomment when GTT notifications are supported in TV

      // response.data.criticalList.forEach((item) => {
      //   // we only support Conditional order authorization for now
      //   if (item.category === 'ConditionalOrders' && item.status === 'unread') {
      //     const notification = this.getGTTNotification(item);
      //     if (notification) {
      //       notification.actions.primary.link = () => {
      //         EdisGTTService.checkIsGTTEdisRequired(notification);
      //       };
      //       notifications.push(notification);
      //     }
      //   }
      // });

      // #endregion

      return this.notifications;
    }
    AnalyticsService.sendEvent(EVENTS.NOTIFICATION_RESPONSE_FAILURE, {
      errorCode: response.error?.code || '',
      errorMessahe: response.error?.message || '',
    });
    return [];
  }

  public handleCustomConfig(
    inAppData: InAppNotification,
    notifications: INotification[],
    item: NotificationConfig,
  ) {
    switch (inAppData.variant) {
      case 'fullscreen':
        notifications.push(this.getFullScreenNotification(item, inAppData));
        break;
      // case 'warning':
      //   notifications.push(this.getWarningNotification(item, inAppData));
      //   break;
      // case 'hybrid': {
      //   // break down the hybrid flow to simple notifications
      //   const [warning, fullScreen] = this.getHybridNotifications(
      //     item,
      //     inAppData,
      //   );
      //   notifications.push(fullScreen);
      //   notifications.push(warning);
      //   break;
      // }
      default:
        break;
    }
  }

  // TODO: Uncomment below methods when TV start supporting Hydbrid notifications
  // private getHybridNotifications(
  //   item: NotificationConfig,
  //   inAppData: InAppNotification,
  // ): [INotification, INotification] {
  //   const warning = this.getWarningNotification(item, inAppData);
  //   warning.id = `${warning.id}-hybrid-bar`;
  //   const fullScreen = this.getFullScreenNotification(item, inAppData);
  //   fullScreen.id = `${fullScreen.id}-hybrid-modal`;

  //   // get start and end time of notifications
  //   const hybridInitialDuration =
  //     inAppData.hybrid_config?.warning_expire_time ?? 0;
  //   const warningEndDate = this.addTime(
  //     inAppData.start_date,
  //     hybridInitialDuration,
  //   );
  //   const fullScreenStartDate = this.addTime(warningEndDate, 1);

  //   // set start and end time
  //   warning.endDate = warningEndDate;
  //   fullScreen.startDate = fullScreenStartDate;

  //   return [warning, fullScreen];
  // }

  // private addTime = (jsonDateTime: string, addMinutes: number) => {
  //   const date = new Date(jsonDateTime);
  //   let mill = date.getMilliseconds();
  //   mill += addMinutes * 60000;
  //   date.setMilliseconds(mill);
  //   return date.toJSON();
  // };

  private getFullscreenCTA = (
    data: FullScreenActions | undefined,
  ): INotificationActions => {
    if (data) {
      const primaryCTA = data.primary_cta || data.right_cta;
      const secondaryCTA = data?.secondary_cta || data?.left_cta;

      const primary: INotificationAction = {
        text: primaryCTA?.text ?? '',
        icon: primaryCTA?.icon,
        action:
          primaryCTA?.action_type === 'open-weblink' ? 'link' : 'dismiss',
        link: primaryCTA?.weblink || null,
        autoLogin: !!primaryCTA?.auto_login,
        theme: ctaThemeMapper[primaryCTA?.bg_theme || 'dark'],
        appendParams: primaryCTA?.should_append_query_params,
      };

      if (secondaryCTA) {
        return {
          primary,
          secondary: {
            text: secondaryCTA.text ?? '',
            icon: secondaryCTA.icon,
            action:
              secondaryCTA.action_type === 'open-weblink'
                ? 'link'
                : 'dismiss',
            link: secondaryCTA.weblink,
            autoLogin: secondaryCTA.auto_login,
            theme: ctaThemeMapper[secondaryCTA?.bg_theme || 'lite'],
            appendParams: secondaryCTA?.should_append_query_params,
          },
        };
      }

      return { primary };
    }

    return {
      primary: {
        text: 'Close',
        icon: false,
        action: 'dismiss',
        link: '',
        autoLogin: false,
      },
    };
  };

  // TODO: Uncomment below method when TV start supporting warning notifications
  // private getWarningNotification(
  //   item: NotificationConfig,
  //   inAppData: InAppNotification,
  // ): INotification {
  //   let persistTill = '';
  //   if (inAppData.warning_config?.persist) {
  //     persistTill = this.addTime(
  //       inAppData.start_date,
  //       inAppData.warning_config.persist,
  //     );
  //   }

  //   return {
  //     id: `${item.id}`,
  //     classification: item.classification,
  //     startDate: inAppData.start_date,
  //     endDate: inAppData.end_date,
  //     title: '',
  //     message: inAppData.warning_config?.message ?? '',
  //     theme: inAppData.warning_config?.theme ?? 'none',
  //     image: '',
  //     actions: {
  //       primary: {
  //         text: inAppData.warning_config?.cta.text ?? '',
  //         icon: inAppData.warning_config?.cta.icon,
  //         action:
  //           inAppData.warning_config?.cta.action_type === 'open-weblink'
  //             ? 'link'
  //             : 'dismiss',
  //         link: inAppData.warning_config?.cta.weblink ?? '',
  //         autoLogin: inAppData.warning_config
  //           ? inAppData.warning_config.cta.auto_login
  //           : false,
  //       },
  //     },
  //     persistTill: persistTill ?? undefined,
  //     variant: 'bar',
  //   };
  // }

  private getFullScreenNotification(
    item: NotificationConfig,
    inAppData: InAppNotification,
  ): INotification {
    return {
      id: `${item.id}`,
      classification: item?.classification,
      startDate: inAppData?.start_date,
      identifier: inAppData.identifier,
      version: inAppData.version,
      endDate: inAppData?.end_date,
      title: inAppData?.fullscreen_config?.title ?? '',
      message: inAppData?.fullscreen_config?.message ?? '',
      theme: inAppData?.fullscreen_config?.theme ?? 'none',
      image: inAppData?.fullscreen_config?.image_link ?? '',
      dismissable: inAppData?.fullscreen_config?.is_dismissable,
      actions: this.getFullscreenCTA(inAppData?.fullscreen_config?.cta),
      variant: 'modal',
    };
  }

  // TODO: Uncomment when GTT notifications are supported in TV
  // private getGTTNotification = (
  //   notification: NotificationConfig,
  // ): INotification | null => {
  //   const isAuthNotify =
  //     notification.conditionalOrderConfig?.type === 'EDIS_AUTH_NOTIFY';
  //   if (isAuthNotify) {
  //     const currentTime = new Date();
  //     const startTime = currentTime.setHours(0, 0, 0, 0);
  //     const endTime = currentTime.setHours(24, 0, 0, 0);
  //     return {
  //       id: `${notification.id}`,
  //       classification: notification.classification,
  //       startDate: new Date(startTime).toJSON(),
  //       endDate: new Date(endTime).toJSON(),
  //       title: notification.title ?? '',
  //       message: notification.desc ?? '',
  //       theme: 'critical',
  //       image: '',
  //       actions: {
  //         primary: {
  //           text: isAuthNotify ? 'Authorize Now' : 'View Order',
  //           action: 'link',
  //           // eslint-disable-next-line @typescript-eslint/no-empty-function
  //           link: () => {},
  //           autoLogin: false,
  //         },
  //       },
  //       variant: 'bar',
  //       persistTill: new Date(endTime).toJSON(),
  //     };
  //   }
  //     return null;
  // };

  private get getDeviceDetailsHeader() {
    const {
      manufacturer,
      os,
      osVersion,
      browserName,
    } = PlatformService.getDeviceDetails();
    const { userAgent } = window.navigator;

    return `platform=WEB|osName=${os}|appVariant=TV|osVersion=${osVersion}|appVersion=${VERSION}|modelName=${browserName}|manufacturer=${manufacturer}|userAgent=${userAgent}|buildVersionCode=3.0`;
  }
}

export default new NotificationService();
